import { motion, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useReveal } from '../../hooks/useReveal';
import { useSnapshot } from '../../context/SnapshotContext';
import { ArrowRight, Target, Workflow, Lightbulb } from 'lucide-react';

const ease = [0.22, 1, 0.36, 1];

const STEPS = [
    { Icon: Target, label: '8 quick questions', sub: 'Type, goal, leak, follow-up' },
    { Icon: Workflow, label: 'Rule-based diagnosis', sub: 'Where the system is leaking' },
    { Icon: Lightbulb, label: 'Five practical next steps', sub: 'In your inbox in two minutes' },
];

export function DiagnosticTeaser() {
    const ref = useReveal();
    const { openSnapshot } = useSnapshot();
    return (
        <section
            id="snapshot-teaser"
            data-testid="diagnostic-teaser-section"
            className="relative section overflow-hidden"
        >
            <div className="atmosphere opacity-60" />
            <div className="shell relative">
                <div ref={ref} className="reveal grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
                    {/* Left — copy */}
                    <div>
                        <p className="eyebrow">Diagnostic · The Growth System Snapshot</p>
                        <h2 className="mt-6 font-display font-semibold text-[2.4rem] sm:text-[3.4rem] lg:text-[4.2rem] leading-[1.02] tracking-[-0.04em] text-white">
                            Find{' '}
                            <span className="text-gradient-cyan">where you're losing clients</span>
                            {' '}before you spend on anything else.
                        </h2>
                        <p className="mt-6 text-[17px] leading-[1.65] text-slate-300 max-w-xl">
                            Eight quick questions. A rule-based diagnosis of
                            your single biggest leak. Five practical next steps
                            you can take this week — whether you hire us or not.
                        </p>

                        <ul className="mt-8 space-y-3">
                            {STEPS.map((s) => (
                                <li key={s.label} className="flex items-start gap-3.5">
                                    <span
                                        className="grid place-items-center w-9 h-9 rounded-lg shrink-0"
                                        style={{
                                            background: 'rgba(6,182,212,0.10)',
                                            border: '1px solid rgba(6,182,212,0.30)',
                                        }}
                                    >
                                        <s.Icon className="w-4 h-4 text-cyan-400" strokeWidth={1.7} />
                                    </span>
                                    <div>
                                        <p className="font-display font-medium text-[15.5px] text-white tracking-[-0.01em]">
                                            {s.label}
                                        </p>
                                        <p className="text-[13.5px] text-slate-400 mt-0.5">
                                            {s.sub}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-10 flex flex-wrap gap-3">
                            <button
                                type="button"
                                data-testid="teaser-open-snapshot"
                                onClick={openSnapshot}
                                className="btn btn-primary"
                            >
                                Find Where You're Losing Clients
                                <ArrowRight className="w-4 h-4" />
                            </button>
                            <a
                                href="mailto:hello@clearadvance.co?subject=Brief%20Your%20Project"
                                data-testid="teaser-brief-project"
                                className="btn btn-ghost"
                            >
                                Brief Your Project
                            </a>
                        </div>
                        <p className="mt-5 font-mono text-[10.5px] tracking-[0.22em] uppercase text-slate-500">
                            2-min diagnostic · Instant clarity · No call required
                        </p>
                    </div>

                    {/* Right — Diagnostic mockup */}
                    <DiagnosticMockup />
                </div>
            </div>
        </section>
    );
}

function DiagnosticMockup() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.3 });
    const [progress, setProgress] = useState(0);
    const [selected, setSelected] = useState(0);

    // Animate progress bar 0 → 44%
    useEffect(() => {
        if (!inView) return;
        const start = performance.now();
        const target = 44;
        const dur = 1400;
        const tick = (now) => {
            const k = Math.min(1, (now - start) / dur);
            const eased = 1 - Math.pow(1 - k, 3);
            setProgress(Math.round(eased * target));
            if (k < 1) requestAnimationFrame(tick);
        };
        const t = setTimeout(() => requestAnimationFrame(tick), 600);
        return () => clearTimeout(t);
    }, [inView]);

    // Cycle the "selected" answer every 2.6s for liveness
    useEffect(() => {
        if (!inView) return;
        const itv = setInterval(() => {
            setSelected((s) => (s + 1) % 3);
        }, 2600);
        return () => clearInterval(itv);
    }, [inView]);

    const OPTIONS = [
        'Leads enquire but do not book',
        'People visit but do not enquire',
        'Follow-up is slow or inconsistent',
    ];

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 30, rotateX: -4 }}
            whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-[520px] mx-auto"
            style={{ transform: 'perspective(1400px) rotateY(-4deg) rotateX(2deg)' }}
        >
            <div
                className="absolute -inset-8 rounded-[2rem] opacity-50"
                style={{ background: 'radial-gradient(ellipse, rgba(6,182,212,0.25), transparent 60%)' }}
            />
            <div
                className="relative rounded-[1.4rem] overflow-hidden"
                style={{
                    background: 'linear-gradient(160deg, rgba(11,18,33,0.95), rgba(5,10,21,0.95))',
                    border: '1px solid rgba(255,255,255,0.10)',
                    boxShadow: '0 50px 100px -30px rgba(6,182,212,0.35), 0 0 0 1px rgba(255,255,255,0.04) inset',
                }}
            >
                {/* Header */}
                <div className="px-5 py-3.5 border-b border-white/[0.08] flex items-center justify-between">
                    <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase">
                        <span className="text-cyan-400">FORM 02</span>
                        <span className="text-slate-500">· Growth System Snapshot</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-cyan-400 pulse-dot" />
                        <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-slate-500">LIVE</span>
                    </div>
                </div>

                {/* Progress */}
                <div className="px-5 py-3 border-b border-white/[0.08]">
                    <div className="flex items-center justify-between font-mono text-[9.5px] tracking-[0.2em] uppercase">
                        <span className="text-slate-400">Step 4 of 9</span>
                        <span className="text-cyan-400 tabular-nums">{progress}%</span>
                    </div>
                    <div className="mt-2 h-[3px] rounded-full bg-white/[0.06] overflow-hidden">
                        <div
                            className="h-full rounded-full transition-[width] duration-150"
                            style={{
                                width: `${progress}%`,
                                background: 'linear-gradient(90deg, #06B6D4, #3B82F6)',
                                boxShadow: '0 0 10px rgba(6,182,212,0.6)',
                            }}
                        />
                    </div>
                </div>

                {/* Body */}
                <div className="p-6">
                    <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-cyan-400">
                        Question
                    </p>
                    <h4 className="mt-2 font-display font-semibold text-[20px] tracking-[-0.025em] text-white leading-[1.2]">
                        Where do you think the biggest leak is right now?
                    </h4>
                    <p className="mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-cyan-400/70">
                        This is where most businesses lose enquiries.
                    </p>

                    <div className="mt-5 space-y-2">
                        {OPTIONS.map((t, i) => {
                            const on = selected === i;
                            return (
                                <div
                                    key={t}
                                    className="flex items-center gap-3 px-3.5 py-3 rounded-lg transition-all duration-500"
                                    style={{
                                        background: on ? 'rgba(6,182,212,0.10)' : 'rgba(255,255,255,0.02)',
                                        border: on ? '1px solid rgba(6,182,212,0.45)' : '1px solid rgba(255,255,255,0.08)',
                                        boxShadow: on ? '0 0 22px -4px rgba(6,182,212,0.35)' : 'none',
                                    }}
                                >
                                    <span
                                        className="grid place-items-center w-4 h-4 rounded-full shrink-0 transition-all duration-500"
                                        style={{
                                            background: on ? '#06B6D4' : 'transparent',
                                            border: on ? '1px solid #06B6D4' : '1px solid rgba(255,255,255,0.18)',
                                        }}
                                    >
                                        {on && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                                    </span>
                                    <span className={`text-[14px] transition-colors duration-500 ${on ? 'text-white' : 'text-slate-400'}`}>
                                        {t}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3.5 border-t border-white/[0.08] flex items-center justify-between">
                    <span className="text-[12.5px] text-slate-500">← Back</span>
                    <span
                        className="px-3.5 py-1.5 rounded-lg text-[12.5px] font-medium text-white inline-flex items-center gap-1.5"
                        style={{
                            background: 'linear-gradient(135deg, #06B6D4, #3B82F6)',
                            boxShadow: '0 0 18px -4px rgba(6,182,212,0.5)',
                        }}
                    >
                        Next
                        <ArrowRight className="w-3 h-3" />
                    </span>
                </div>
            </div>
        </motion.div>
    );
}
